import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { 
  ArrowLeft, Edit, Download, Send, 
  CheckCircle, Trash2, Printer, Link2, Check,
  Copy, Mail, Repeat, Loader2, X
} from "lucide-react";

const InvoiceView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const { data } = await axios.get(`${API}/invoices/${id}`);
      setInvoice(data);
    } catch (err) {
      console.error("Failed to fetch invoice:", err);
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkSent = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/invoices/${id}/send`);
      await fetchInvoice();
    } catch (err) {
      console.error("Failed to mark as sent:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/invoices/${id}/mark-paid`);
      await fetchInvoice();
    } catch (err) {
      console.error("Failed to mark as paid:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API}/invoices/${id}`);
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to delete invoice:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download PDF:", err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopyShareLink = async () => {
    try {
      let shareToken = invoice.share_token;
      if (!shareToken) {
        const { data } = await axios.post(`${API}/invoices/${id}/share`);
        shareToken = data.share_token;
        setInvoice(prev => ({ ...prev, share_token: shareToken }));
      }
      const shareUrl = `${window.location.origin}/invoice/${shareToken}`;
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy share link:", err);
    }
  };

  const handleSendEmail = async () => {
    setEmailSending(true);
    try {
      await axios.post(`${API}/invoices/${id}/email`, { message: emailMessage });
      setEmailSent(true);
      setShowEmailModal(false);
      setEmailMessage("");
      await fetchInvoice();
      setTimeout(() => setEmailSent(false), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to send email";
      alert(detail);
    } finally {
      setEmailSending(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      const { data } = await axios.post(`${API}/invoices/${id}/duplicate`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to duplicate";
      alert(detail);
    }
  };

  const handleSetRecurring = async (frequency) => {
    try {
      await axios.post(`${API}/recurring`, { invoice_id: id, frequency });
      alert(`Recurring invoice set to ${frequency}. New invoices will be auto-created.`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to set recurring";
      alert(detail);
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const classes = {
      draft: "badge badge-draft",
      sent: "badge badge-sent",
      paid: "badge badge-paid",
      overdue: "badge badge-overdue"
    };
    return <span className={classes[status] || classes.draft}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <p className="text-[15px] text-[#6B7280]">Loading invoice...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <p className="text-[15px] text-[#6B7280]">Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40 no-print">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6">
          {/* Top row: back + invoice number + primary actions */}
          <div className="h-14 sm:h-16 flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 text-[14px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="font-mono font-semibold text-[14px] sm:text-[16px] text-[#0A0A0A]">{invoice.invoice_number}</span>
                {getStatusBadge(invoice.status)}
              </div>
            </div>
            {/* Primary actions: visible on all sizes */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {invoice.status === "draft" && (
                <button
                  onClick={() => setShowEmailModal(true)}
                  disabled={actionLoading}
                  className="btn-primary flex items-center gap-1.5 text-[13px] px-2.5 sm:px-3 py-2"
                  data-testid="send-email-btn"
                >
                  <Mail className="w-4 h-4" />
                  <span className="hidden sm:inline">Send</span>
                </button>
              )}
              {invoice.status === "draft" && (
                <button
                  onClick={handleMarkSent}
                  disabled={actionLoading}
                  className="btn-outline flex items-center gap-1.5 text-[13px] px-2.5 sm:px-3 py-2"
                  data-testid="mark-sent-btn"
                >
                  <Send className="w-4 h-4" />
                  <span className="hidden sm:inline">Mark sent</span>
                </button>
              )}
              {invoice.status !== "draft" && invoice.status !== "paid" && (
                <button
                  onClick={() => setShowEmailModal(true)}
                  disabled={actionLoading}
                  className="btn-primary flex items-center gap-1.5 text-[13px] px-2.5 sm:px-3 py-2"
                  data-testid="resend-email-btn"
                >
                  <Mail className="w-4 h-4" />
                  <span className="hidden sm:inline">Resend</span>
                </button>
              )}
              {(invoice.status === "sent" || invoice.status === "overdue") && (
                <button
                  onClick={handleMarkPaid}
                  disabled={actionLoading}
                  className="px-2.5 sm:px-3 py-2 bg-[#10B981] text-white font-semibold text-[13px] hover:bg-[#059669] flex items-center gap-1.5 transition-colors"
                  data-testid="mark-paid-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">Mark paid</span>
                </button>
              )}
              {/* Secondary icon actions */}
              <button
                onClick={handleDuplicate}
                className="p-2 border border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
                title="Duplicate invoice"
                data-testid="duplicate-btn"
              >
                <Copy className="w-4 h-4 text-[#6B7280]" />
              </button>
              <button
                onClick={handleCopyShareLink}
                className={`p-2 border transition-colors ${linkCopied ? 'border-[#10B981] bg-[#ECFDF5]' : 'border-[#E5E7EB] hover:bg-[#F9FAFB]'}`}
                title={linkCopied ? "Link copied!" : "Copy share link"}
                data-testid="share-link-btn"
              >
                {linkCopied ? <Check className="w-4 h-4 text-[#10B981]" /> : <Link2 className="w-4 h-4 text-[#6B7280]" />}
              </button>
              <button
                onClick={handleDownloadPDF}
                className="p-2 border border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
                title="Download PDF"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 text-[#6B7280]" />
              </button>
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="p-2 border border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
                  title="Print"
                  data-testid="print-btn"
                >
                  <Printer className="w-4 h-4 text-[#6B7280]" />
                </button>
                <Link
                  to={`/invoices/${id}/edit`}
                  className="p-2 border border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
                  title="Edit"
                  data-testid="edit-btn"
                >
                  <Edit className="w-4 h-4 text-[#6B7280]" />
                </Link>
                <button
                  onClick={handleDelete}
                  disabled={actionLoading}
                  className="p-2 border border-[#FECACA] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                  title="Delete"
                  data-testid="delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          {/* Mobile-only secondary actions row */}
          <div className="sm:hidden flex items-center gap-2 pb-2 -mt-1">
            <button
              onClick={handlePrint}
              className="p-2 border border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
              title="Print"
              data-testid="print-btn-mobile"
            >
              <Printer className="w-4 h-4 text-[#6B7280]" />
            </button>
            <Link
              to={`/invoices/${id}/edit`}
              className="p-2 border border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
              title="Edit"
              data-testid="edit-btn-mobile"
            >
              <Edit className="w-4 h-4 text-[#6B7280]" />
            </Link>
            <button
              onClick={handleDelete}
              disabled={actionLoading}
              className="p-2 border border-[#FECACA] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
              title="Delete"
              data-testid="delete-btn-mobile"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Invoice Preview */}
      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="invoice-preview bg-white border border-[#E5E7EB] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-4 sm:p-8 lg:p-12">
          {/* Invoice Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8 sm:mb-12">
            <div className="flex items-center gap-3">
              {user?.logo_url ? (
                <img 
                  src={`${API.replace('/api', '')}${user.logo_url}`}
                  alt="Logo"
                  className="w-10 h-10 object-contain flex-shrink-0"
                />
              ) : user?.business_initials ? (
                <div className="w-10 h-10 bg-[#1A3DFF] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-[14px]">{user.business_initials}</span>
                </div>
              ) : null}
              <div>
                <h1 className="font-display text-[18px] sm:text-[22px] tracking-[-0.03em] text-[#0A0A0A] mb-1">
                  {invoice.user?.business_name || invoice.user?.name || ""}
                </h1>
                {invoice.user?.email && (
                  <p className="text-[13px] sm:text-[14px] text-[#6B7280]">{invoice.user.email}</p>
                )}
                {invoice.user?.business_address && (
                  <p className="text-[12px] sm:text-[13px] text-[#6B7280] whitespace-pre-line">{invoice.user.business_address}</p>
                )}
              </div>
            </div>
            <div className="sm:text-right">
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-1">Invoice</p>
              <p className="font-mono font-semibold text-[16px] sm:text-[18px] text-[#0A0A0A]">{invoice.invoice_number}</p>
            </div>
          </div>

          {/* Bill To & Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div>
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-2">Bill to</p>
              {invoice.client && (
                <>
                  <p className="font-semibold text-[16px] text-[#0A0A0A]">{invoice.client.name}</p>
                  {invoice.client.company && <p className="text-[14px] text-[#6B7280]">{invoice.client.company}</p>}
                  <p className="text-[14px] text-[#6B7280]">{invoice.client.email}</p>
                  {invoice.client.address && <p className="text-[14px] text-[#6B7280]">{invoice.client.address}</p>}
                  {invoice.client.city && <p className="text-[14px] text-[#6B7280]">{invoice.client.city}</p>}
                </>
              )}
            </div>
            <div className="sm:text-right">
              <div className="inline-block text-left">
                <div className="mb-3 flex sm:block gap-6">
                  <div>
                    <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Issue date</p>
                    <p className="font-mono text-[14px] text-[#0A0A0A]">{invoice.issue_date}</p>
                  </div>
                  <div className="sm:mt-3">
                    <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Due date</p>
                    <p className="font-mono text-[14px] text-[#0A0A0A]">{invoice.due_date}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8 overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full min-w-[400px] px-4 sm:px-0">
              <thead>
                <tr className="border-b border-[#E5E7EB]">
                  <th className="text-left py-3 font-semibold text-[11px] text-[#9CA3AF] tracking-[0.05em] uppercase">Description</th>
                  <th className="text-right py-3 font-semibold text-[11px] text-[#9CA3AF] tracking-[0.05em] uppercase w-20">Qty</th>
                  <th className="text-right py-3 font-semibold text-[11px] text-[#9CA3AF] tracking-[0.05em] uppercase w-28">Rate</th>
                  <th className="text-right py-3 font-semibold text-[11px] text-[#9CA3AF] tracking-[0.05em] uppercase w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index} className="border-b border-[#E5E7EB]">
                    <td className="py-4 text-[14px] text-[#0A0A0A]">{item.description}</td>
                    <td className="text-right py-4 font-mono text-[14px] text-[#0A0A0A]">{item.quantity}</td>
                    <td className="text-right py-4 font-mono text-[14px] text-[#0A0A0A]">{formatCurrency(item.rate, invoice.currency)}</td>
                    <td className="text-right py-4 font-mono font-semibold text-[14px] text-[#0A0A0A]">{formatCurrency(item.amount, invoice.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-64">
              <div className="flex justify-between py-2">
                <span className="text-[14px] text-[#6B7280]">Subtotal</span>
                <span className="font-mono text-[14px] text-[#0A0A0A]">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
              </div>
              {invoice.tax_rate > 0 && !(invoice.taxes && invoice.taxes.length > 0) && (
                <div className="flex justify-between py-2">
                  <span className="text-[14px] text-[#6B7280]">Tax ({invoice.tax_rate}%)</span>
                  <span className="font-mono text-[14px] text-[#0A0A0A]">{formatCurrency(invoice.tax_amount, invoice.currency)}</span>
                </div>
              )}
              {invoice.taxes && invoice.taxes.length > 0 && invoice.taxes.map((t, i) => (
                <div key={i} className="flex justify-between py-2">
                  <span className="text-[14px] text-[#6B7280]">{t.label} ({t.rate}%)</span>
                  <span className="font-mono text-[14px] text-[#0A0A0A]">{formatCurrency(t.amount, invoice.currency)}</span>
                </div>
              ))}
              {invoice.discount > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-[14px] text-[#6B7280]">Discount</span>
                  <span className="font-mono text-[14px] text-[#0A0A0A]">-{formatCurrency(invoice.discount, invoice.currency)}</span>
                </div>
              )}
              <div className="flex justify-between py-3 border-t border-[#0A0A0A] mt-2">
                <span className="font-semibold text-[15px] text-[#0A0A0A]">Total</span>
                <span className="font-display text-[24px] tracking-[-0.02em] text-[#0A0A0A]">{formatCurrency(invoice.total, invoice.currency)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="border-t border-[#E5E7EB] pt-6">
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-2">Notes</p>
              <p className="text-[14px] text-[#6B7280] whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}
        </div>

        {/* Recurring Invoice Section */}
        <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-4 no-print">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Repeat className="w-4 h-4 text-[#6B7280]" />
              <span className="text-[14px] font-semibold text-[#0A0A0A]">Make Recurring</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {["weekly", "monthly", "quarterly", "yearly"].map(freq => (
                <button
                  key={freq}
                  onClick={() => handleSetRecurring(freq)}
                  className="px-3 py-1.5 text-[12px] font-medium text-[#6B7280] border border-[#E5E7EB] hover:bg-[#F9FAFB] hover:text-[#0A0A0A] transition-colors capitalize"
                  data-testid={`recurring-${freq}-btn`}
                >
                  {freq}
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Email Sent Banner */}
      {emailSent && (
        <div className="fixed bottom-6 right-6 bg-[#ECFDF5] border border-[#10B981] px-6 py-3 flex items-center gap-2 shadow-lg z-50" data-testid="email-sent-banner">
          <CheckCircle className="w-4 h-4 text-[#059669]" />
          <span className="text-[14px] font-medium text-[#065F46]">Invoice emailed successfully</span>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="email-modal">
          <div className="bg-white border border-[#E5E7EB] w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-[16px] text-[#0A0A0A]">Send Invoice</h3>
              <button onClick={() => setShowEmailModal(false)} className="text-[#6B7280] hover:text-[#0A0A0A]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-[13px] text-[#6B7280] mb-1">To</p>
              <p className="text-[14px] font-medium text-[#0A0A0A]">{invoice.client?.email || "No email"}</p>
            </div>
            <div className="mb-4">
              <p className="text-[13px] text-[#6B7280] mb-1">Invoice</p>
              <p className="text-[14px] font-mono text-[#0A0A0A]">{invoice.invoice_number} — {formatCurrency(invoice.total, invoice.currency)}</p>
            </div>
            <div className="mb-6">
              <label className="text-[13px] text-[#6B7280] block mb-1">Message (optional)</label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Add a personal message..."
                className="w-full border border-[#E5E7EB] p-3 text-[14px] h-24 resize-none focus:outline-none focus:border-[#1A3DFF]"
                data-testid="email-message-input"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 py-2.5 border border-[#E5E7EB] text-[14px] font-medium text-[#6B7280] hover:bg-[#F9FAFB] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailSending || !invoice.client?.email}
                className="flex-1 py-2.5 bg-[#1A3DFF] text-white text-[14px] font-bold hover:bg-[#0A0A0A] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="confirm-send-email-btn"
              >
                {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {emailSending ? "Sending..." : "Send Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceView;
